import { BreakpointObserver, MediaMatcher } from "@angular/cdk/layout";
import {
  ChangeDetectorRef,
  Component,
  effect,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { Subscription } from "rxjs";
import { MatSidenav, MatSidenavContent } from "@angular/material/sidenav";
import { MatDialog } from "@angular/material/dialog";
import { ContactUsComponent } from "src/app/shared/components/contact-us/contact-us.component";
import { CoreService } from "src/app/services/core.service";
import { AppSettings } from "src/app/config";
import { filter, finalize } from "rxjs/operators";
import { NavigationEnd, Router } from "@angular/router";
import { navItems } from "./vertical/sidebar/sidebar-data";
import { NavService } from "../../services/nav.service";
import { AppNavItemComponent } from "./vertical/sidebar/nav-item/nav-item.component";
import { RouterModule } from "@angular/router";
import { MaterialModule } from "src/app/material.module";
import { StoreService } from "src/app/services/apps/store/store.service";
import { ProviderService } from "src/app/services/apps/providers/provider.service";
import { forkJoin } from "rxjs";
import { CommonModule } from "@angular/common";
import { SidebarComponent } from "./vertical/sidebar/sidebar.component";
import { NgScrollbarModule } from "ngx-scrollbar";
import { TablerIconsModule } from "angular-tabler-icons";
import { HeaderComponent } from "./vertical/header/header.component";
import { AppHorizontalHeaderComponent } from "./horizontal/header/header.component";
import { AppHorizontalSidebarComponent } from "./horizontal/sidebar/sidebar.component";
import { SessionService } from "src/app/services/session.service";
import { WebSocketNotificationOrchestratorService } from "src/app/shared/services/websocket-notification-orchestrator.service";
import { LoaderService } from "src/app/shared/components/loader/loader.service";
import { fetchAuthSession } from "aws-amplify/auth";
import { TranslateModule } from "@ngx-translate/core";
import { UsageOverviewSidenavComponent } from "src/app/pages/billing/billing-status-widget/usage-overview-sidenav/usage-overview-sidenav.component";
import { BillingService } from "src/app/pages/billing/billing.service";
import { isBillingDelegated } from "src/app/pages/billing/billing.model";
import { isUserOnboardingInProgress } from "src/app/pages/profile/profile.model";
import { ProfileService } from "src/app/pages/profile/profile.service";
import { NavItem, getNavItemTrackId } from "./vertical/sidebar/nav-item/nav-item";

const MOBILE_VIEW = "screen and (max-width: 768px)";
const TABLET_VIEW = "screen and (min-width: 769px) and (max-width: 1024px)";
const MONITOR_VIEW = "screen and (min-width: 1024px)";
const BELOWMONITOR = "screen and (max-width: 1023px)";

@Component({
  standalone: true,
  selector: "app-full",
  imports: [
    RouterModule,
    AppNavItemComponent,
    MaterialModule,
    CommonModule,
    SidebarComponent,
    NgScrollbarModule,
    TablerIconsModule,
    HeaderComponent,
    AppHorizontalHeaderComponent,
    AppHorizontalSidebarComponent,
    TranslateModule,
    UsageOverviewSidenavComponent,
  ],
  templateUrl: "./full.component.html",
  styles: [
    `
      .blocked-nav-caption {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0.625rem 12px 0.5rem;
        margin-top: 0.625rem;
        border-top: 1px solid rgba(0, 0, 0, 0.12);
        font-size: 0.6875rem;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
      }

      .blocked-nav-caption mat-icon {
        font-size: 1rem;
        margin-left: 8px;
      }
    `,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class FullComponent implements OnInit, OnDestroy, AfterViewInit {
  private static readonly FULL_WIDTH_LIST_ROUTES = new Set([
    "/store/cases",
    "/store/claims",
    "/store/cogs",
    "/claim/damaged-inventory",
    "/claim/refund-never-returned-claim",
    "/claim/reanalysis",
    "/claim/lost-outbound",
    "/claim/storage-fees",
    "/report/cases",
    "/report/cases/export-history",
    "/report/claims",
    "/report/claims/export-history",
    "/report/reimbursements",
    "/report/reimbursements/export-history",
    // "/report/inbound-outbound", // Temporarily disabled
  ]);

  navItems = navItems;
  unblockedNavItems: NavItem[] = [];
  blockedNavItems: NavItem[] = [];
  protected isDelegatedUser = false;

  @ViewChild("leftsidenav")
  public sidenav: MatSidenav;
  resView = false;
  isDashboardRoute = false;
  isFullWidthListRoute = false;
  @ViewChild("content", { static: true }) content!: MatSidenavContent;
  //get options from service
  options = this.settings.getOptions();
  private layoutChangesSubscription = Subscription.EMPTY;
  private readonly settingsSubscription = new Subscription();
  private isMobileScreen = false;
  private isContentWidthFixed = true;
  private isCollapsedWidthFixed = false;
  private htmlElement!: HTMLHtmlElement;
  private sidebarHoverExpanded = false;
  private sidebarCollapsedBeforeHover = false;
  /** Blocks JS + CSS mini hover-expand until pointer leaves sidenav. */
  protected sidebarMiniHoverDisabled = false;

  get isOver(): boolean {
    return this.isMobileScreen;
  }

  readonly getNavItemTrackId = getNavItemTrackId;

  get hasBlockedNavMenuItems(): boolean {
    return this.blockedNavItems.some((item) => !item.navCap);
  }

  get isTablet(): boolean {
    return this.resView;
  }

  /** Desktop side nav stays open; mobile overlay uses `sidenavOpened` from settings. */
  get isVerticalSidenavOpened(): boolean {
    if (this.options.horizontal || this.options.navPos !== "side") {
      return false;
    }

    if (this.resView || this.isOver) {
      return this.options.sidenavOpened;
    }

    return true;
  }

  user = {
    initials: "",
    email: "",
    name: "",
  };

  constructor(
    private settings: CoreService,
    private mediaMatcher: MediaMatcher,
    private router: Router,
    private breakpointObserver: BreakpointObserver,
    private navService: NavService,
    private sessionService: SessionService,
    private wsOrchestrator: WebSocketNotificationOrchestratorService,
    private loaderService: LoaderService,
    private dialog: MatDialog,
    private storeService: StoreService,
    private providerService: ProviderService,
    private profileService: ProfileService,
    private billingService: BillingService,
    private cdr: ChangeDetectorRef,
  ) {
    this.htmlElement = document.querySelector("html")!;
    this.layoutChangesSubscription = this.breakpointObserver
      .observe([MOBILE_VIEW, TABLET_VIEW, MONITOR_VIEW, BELOWMONITOR])
      .subscribe((state) => {
        this.isMobileScreen = state.breakpoints[MOBILE_VIEW];
        this.resView = state.breakpoints[MOBILE_VIEW];
        // Desktop/tablet side nav must stay open; mobile overlay starts closed.
        // Persist via CoreService so localStorage `sidenavOpened: false` from mobile
        // does not leave desktop with a closed drawer and no menu button.
        const sidenavOpened = !this.isMobileScreen;
        this.settings.setOptions({ sidenavOpened });
        this.options = this.settings.getOptions();
        if (this.options.sidenavCollapsed === false) {
          this.settings.setOptions({
            sidenavCollapsed: state.breakpoints[TABLET_VIEW],
          });
          this.options = this.settings.getOptions();
        }
        this.isContentWidthFixed = state.breakpoints[MONITOR_VIEW];
        this.ensureVerticalSidenavOpen();
        this.cdr.markForCheck();
      });

    // Initialize project theme with options
    this.receiveOptions(this.options);

    this.updateIsDashboardRoute(this.router.url);
    this.updateIsFullWidthListRoute(this.router.url);

    // This is for scroll to top
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        this.content.scrollTo({ top: 0 });
        this.updateIsDashboardRoute((event as NavigationEnd).urlAfterRedirects);
        this.updateIsFullWidthListRoute((event as NavigationEnd).urlAfterRedirects);
      });

    effect(() => {
      const stores = this.storeService.stores() ?? [];
      const providers = this.providerService.providers() ?? [];
      const isOnboardingIncomplete = isUserOnboardingInProgress(
        this.profileService.userProfile(),
      );

      const isDelegated = isBillingDelegated(
        this.billingService.getPreloadedBilling()?.customer,
      );
      this.isDelegatedUser = isDelegated;

      const baseItems = isDelegated
        ? navItems.filter((item) => item.displayName !== "Billing")
        : navItems;

      const processedNav = this.navService.buildNavItemsWithDynamicClaims(
        baseItems,
        stores,
        providers,
        isOnboardingIncomplete,
      );

      this.navItems = processedNav.navItems;
      this.unblockedNavItems = processedNav.unblocked;
      this.blockedNavItems = processedNav.blocked;
      this.navService.updateNavItems(processedNav.navItems);
    });
  }
  async logOut() {
    this.sessionService.signOutUser().then(() => {
      this.wsOrchestrator.shutdown();
    });
  }

  ngAfterViewInit(): void {
    this.ensureVerticalSidenavOpen();
  }

  async ngOnInit() {
    this.settingsSubscription.add(
      this.settings.notify.subscribe(() => {
        this.options = this.settings.getOptions();
        this.cdr.markForCheck();
      }),
    );

    this.settingsSubscription.add(
      forkJoin({
        stores: this.storeService.getCachedOrLoad(),
        providers: this.providerService.getCachedOrLoad(),
      }).subscribe(),
    );

    await this.wsOrchestrator.initialize();
    try {
      // Read the profile from the ID token rather than calling Cognito GetUser.
      // GetUser needs the aws.cognito.signin.user.admin scope on the access
      // token; hosted-UI/federated sign-in (Sign in with Amazon) issues a token
      // with only the OAuth scopes, so fetchUserAttributes() threw
      // "Access Token does not have required scopes" and every Amazon user fell
      // through to the "U"/"User" placeholder. The ID token already carries
      // these claims, and it is the token the app sends to the API anyway.
      const session = await fetchAuthSession();
      const claims = session.tokens?.idToken?.payload ?? {};
      const userAttributes = {
        name: typeof claims["name"] === "string" ? claims["name"] : undefined,
        given_name:
          typeof claims["given_name"] === "string"
            ? claims["given_name"]
            : undefined,
        email: typeof claims["email"] === "string" ? claims["email"] : undefined,
      };
      // Get initials from name property
      const fullName = userAttributes.name || userAttributes.given_name || "";
      const names = fullName.trim().split(" ");
      const initials = names
        .map((n) => n[0]?.toUpperCase())
        .join("")
        .slice(0, 2);

      this.user = {
        initials:
          initials || userAttributes.email?.charAt(0)?.toUpperCase() || "U",
        email: userAttributes.email || "",
        name:
          fullName ||
          userAttributes.email?.slice(0, userAttributes.email.indexOf("@")) ||
          "User",
      };
    } catch (error) {
      console.error("Error initializing full component:", error);
      // Fallback en caso de error
      this.user = {
        initials: "U",
        email: "",
        name: "User",
      };
    }
  }

  ngOnDestroy() {
    this.layoutChangesSubscription.unsubscribe();
    this.settingsSubscription.unsubscribe();
  }

  toggleCollapsed() {
    this.isContentWidthFixed = false;
    const willCollapse = !this.settings.getOptions().sidenavCollapsed;

    this.sidebarHoverExpanded = false;

    if (willCollapse) {
      this.sidebarMiniHoverDisabled = true;
      this.sidebarCollapsedBeforeHover = true;
    } else {
      this.sidebarMiniHoverDisabled = false;
    }

    this.settings.setOptions({ sidenavCollapsed: willCollapse });
    this.options = this.settings.getOptions();
  }

  resetCollapsedState(timer = 400) {
    setTimeout(() => {
      this.settings.setOptions(this.options);
      this.options = this.settings.getOptions();
    }, timer);
  }

  onSidenavClosedStart() {
    this.isContentWidthFixed = false;
  }

  onSidenavOpenedChange(isOpened: boolean) {
    this.isCollapsedWidthFixed = !this.isOver;

    const isDesktopSideNav =
      !this.options.horizontal &&
      this.options.navPos === "side" &&
      !this.isMobileScreen &&
      !this.resView;

    if (!isOpened && isDesktopSideNav) {
      this.ensureVerticalSidenavOpen();
      return;
    }

    this.settings.setOptions({ sidenavOpened: isOpened });
    this.options = this.settings.getOptions();
  }

  /** Close overlay sidenav after navigation — mobile only, not tablet side mode. */
  closeSidenavIfOverlay(): void {
    if (this.isOver || this.resView) {
      this.sidenav?.toggle();
    }
  }

  receiveOptions(options: AppSettings): void {
    this.toggleDarkTheme(options);
    this.toggleColorsTheme(options);
  }

  toggleDarkTheme(options: AppSettings) {
    if (options.theme === "dark") {
      this.htmlElement.classList.add("dark-theme");
      this.htmlElement.classList.remove("light-theme");
    } else {
      this.htmlElement.classList.remove("dark-theme");
      this.htmlElement.classList.add("light-theme");
    }
  }

  toggleColorsTheme(options: AppSettings) {
    this.htmlElement.classList.forEach((className) => {
      if (className.endsWith("_theme")) {
        this.htmlElement.classList.remove(className);
      }
    });

    this.htmlElement.classList.add(options.activeTheme);
  }

  onSuport() {
    this.loaderService.show();
    this.storeService
      .get()
      .pipe(finalize(() => this.loaderService.hide()))
      .subscribe({
        next: (stores: any) => {
          this.dialog.open(ContactUsComponent, {
            autoFocus: false,
            disableClose: true,
            data: { stores },
          });
        },
        error: () => {
          // TODO: handle error if required before opening the modal
        },
      });
  }

  onSidebarMouseEnter() {
    if (this.sidebarMiniHoverDisabled) {
      return;
    }

    if (this.settings.getOptions().sidenavCollapsed && !this.sidebarHoverExpanded) {
      this.sidebarCollapsedBeforeHover = true;
      this.sidebarHoverExpanded = true;
      this.settings.setOptions({ sidenavCollapsed: false });
      this.options = this.settings.getOptions();
    }
  }

  onSidebarMouseLeave() {
    this.sidebarMiniHoverDisabled = false;

    if (this.sidebarHoverExpanded) {
      this.sidebarHoverExpanded = false;
      this.settings.setOptions({
        sidenavCollapsed: this.sidebarCollapsedBeforeHover,
      });
      this.options = this.settings.getOptions();
    }
  }

  private updateIsDashboardRoute(url: string): void {
    const path = url.split("?")[0];
    this.isDashboardRoute = path === "/dashboard" || path === "/";
  }

  private updateIsFullWidthListRoute(url: string): void {
    const path = url.split("?")[0];
    this.isFullWidthListRoute = FullComponent.FULL_WIDTH_LIST_ROUTES.has(path);
  }

  private ensureVerticalSidenavOpen(): void {
    if (!this.isVerticalSidenavOpened) {
      return;
    }

    if (!this.options.sidenavOpened) {
      this.settings.setOptions({ sidenavOpened: true });
      this.options = this.settings.getOptions();
    }

    queueMicrotask(() => {
      if (this.sidenav && !this.sidenav.opened) {
        void this.sidenav.open();
      }
      this.cdr.markForCheck();
    });
  }
}
